/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Axios from 'axios';
import Vue from 'vue';
import Router from 'vue-router';
import PopupProvider from '../providers/PopupProvider';
import RouterStorageService from '../services/RouterStorageService';
import RouteNames from '../src/routes/route-name';
import store from '../src/store/store';
import OnboardingApi from '../api-v2/api/onboarding';
import AclName from './acl/AclName.js';
import { DEDUCTION_DATE_FIELD, ENTRY_DATE_FIELD } from './constants/filters/dateFilterFields';
import evidenceType from './constants-v2/bookkeeping/evidence-type.js';
import { isSystemBalanceRefreshRequired } from './constants/systemBalance';
import { hasFileModuleActive } from './helpers/RouteHelpers.js';
import { DOCUMENT_CATEGORIES_ROUTES } from './modules/document-categories/constants/routes';
import { EVIDENCE_DESCRIPTIONS_ROUTES } from './modules/evidence-descriptions/constants/routes';
import { ksefRoutes } from './modules/ksef/routes';
import beforeZusEnterMiddleware from './router/beforeZusEnterMiddleware';
import crmNavigationMiddleware from './router/crmNavigationMiddleware.js';
import filterMiddleware from './router/filterMiddleware.js';
import routes from './routes';
import routeName from './routes/routeName';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/changediary',
          name: 'changediary',
          components: {
            default: () => import('./views/pages/changediary/ChangelogView'),
            topRightSection: () => import('./views/pages/changediary/components/ChangelogTopRightSection'),
          },
          meta: {
            breadcrumb: [
              { title: 'Dziennik zmian', url: '/changediary' },
            ],
          },
        },
        {
          path: '/',
          name: routeName.home,
          component: () => import('./views/pages/AccountLandingPage/AccountLandingPage.vue'),
        },
        {
          path: '/how-to-start',
          name: routeName.howToStart,
          component: () => import('./views/pages/how-to-start/HowToStartPage.vue'),
        },
        {
          path: '/hello',
          name: 'hello',
          component: () => import('./views/pages/AccoutingOfficeAgreement/NewAoAgreementLandingPage.vue'),
          meta: {
            breadcrumb: [
              { title: 'Przejrzyj umowę', url: '/hello' },
            ],
          },
        },

        {
          path: '/account',
          name: 'account-landing-page',
          component: () => import('./views/pages/AccountLandingPage/AccountLandingPage.vue'),
        },

        {
          path: '/files',
          name: 'AccountingClientFiles',
          components: {
            default: () => import('./views/pages/AccountingClientFiles/AccountingClientFilesView'),
            topRightSection: () => import('./views/pages/AccountingClientFiles/AccountingClientFilesTopRightSection'),
          },
          meta: {
            acl: AclName.FilesPageFilesList,
            breadcrumb: [
              { title: 'Moje pliki', url: '/files' },
            ],
          },
        },
        {
          path: '/files/documents',
          name: 'Documents',
          components: {
            default: () => import('./views/pages/documents/DocumentsView.vue'),
            topRightSection: () => import('./views/pages/documents/DocumentsTopRightSection.vue'),
          },
          meta: {
            acl: AclName.FilesPageFilesList,
            pageTitle: 'Dokumenty',
            breadcrumb: [
              { title: 'Moje pliki', url: '/files' },
              { title: 'Dokumenty', url: '/files/documents' },
            ],
          },
        },
        {
          // Ścieżka analogiczna do archiwum KSeF (`/archive/ksef/invoices/purchase`).
          path: '/archive/documents',
          name: 'archived-documents',
          component: () => import('./views/pages/documents/ArchivedDocumentsView.vue'),
          meta: {
            acl: AclName.FilesPageFilesList,
            pageTitle: 'Archiwum dokumentów',
            breadcrumb: [
              { title: 'Archiwum', url: '/archive/documents' },
              { title: 'Dokumenty', url: '/archive/documents' },
            ],
          },
        },
        {
          path: '/files/documents/add',
          name: 'DocumentsAdd',
          component: () => import('./views/pages/documents/DocumentAddView.vue'),
          meta: {
            acl: AclName.FilesPageFilesFormAdd,
            pageTitle: 'Dodawanie dokumentu',
            breadcrumb: [
              { title: 'Moje pliki', url: '/files' },
              { title: 'Dokumenty', url: '/files/documents' },
              { title: 'Dodawanie', url: '/files/documents/add' },
            ],
          },
        },
        {
          path: '/files/documents/edit/:id',
          name: 'DocumentsEdit',
          component: () => import('./views/pages/documents/DocumentEditView.vue'),
          meta: {
            acl: AclName.FilesPageFilesFormEdit,
            pageTitle: 'Edycja dokumentu',
            breadcrumb: [
              { title: 'Moje pliki', url: '/files' },
              { title: 'Dokumenty', url: '/files/documents' },
              { title: 'Edycja', url: '/files/documents' },
            ],
          },
        },
        {
          path: '/files/add',
          name: 'AccountingClientFilesAdd',
          component: () => import('./views/pages/AccountingClientFiles/AccountingClientFilesAddView'),
          meta: {
            acl: AclName.FilesPageFilesFormAdd,
            breadcrumb: [
              { title: 'Moje pliki', url: '/files' },
              { title: 'Dodawanie', url: '/files/add' },
            ],
          },
        },
        {
          path: '/files/edit/:id',
          name: 'AccountingClientFilesEdit',
          component: () => import('./views/pages/AccountingClientFiles/AccountingClientFilesEditView'),
          meta: {
            acl: AclName.FilesPageFilesFormEdit,
            breadcrumb: [
              { title: 'Moje pliki', url: '/files' },
              { title: 'Edycja', url: '/files/edit/:id' },
            ],
          },
        },
        {
          path: '/files/import-report/:id',
          name: 'AccountingClientFilesImportReport',
          components: {
            default: () => import('./views/pages/AccountingClientFiles/AccountingClientFilesImportReportView'),
          },
          meta: {
            acl: AclName.FilesPageFilesImportReport,
            breadcrumb: [
              { title: 'Moje pliki', url: '/files' },
              { title: 'Raport importu', url: '/files/:id' },
            ],
          },
        },
        {
          path: '/mail-attachments',
          name: 'AccountingClientFiles',
          components: {
            default: () => import('./views/pages/AccountingClientFiles/MailAttachments'),
            topRightSection: () => import('./components-v2/top-right-section.vue'),
          },
          meta: {
            acl: AclName.FilesPageFilesList,
            breadcrumb: [
              { title: 'Załączniki', url: '/mail-attachments' },
            ],
          },
        },
        ...ksefRoutes,
        {
          path: '/invoices',
          name: 'invoices',
          components: {
            default: () => import('./views/pages/invoices/List.vue'),
            topRightSection: () => import('./views/pages/invoices/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesList,
            paginationGroupName: 'invoices-index',
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices' },
              { title: 'Manualne', url: '/invoices' },
            ],
            rule: 'editor',
            search: true,
            module: 'INVOICES',
          },
        },
        {
          path: '/invoices/import',
          name: 'invoices-import',
          components: {
            default: () => import('./views/pages/invoices/import/InvoicesImport.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesList,
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices' },
              { title: 'Manualne', url: '/invoices' },
              { title: 'Import faktur', url: '/invoices/import' },
            ],
            rule: 'editor',
          },
        },
        {
          path: '/evidences/ryczalt',
          name: 'evidences-ryczalt-index',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/ryczalt/RyczaltList.vue'),
            topRightSection: () => import('./views/pages/evidences/ryczalt/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-index',
            breadcrumb: [
              { title: 'Ryczalt', url: '/evidences/ryczalt' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: ENTRY_DATE_FIELD,
          },
        },
        {
          path: '/evidences/ryczalt-sales',
          name: 'evidences-ryczalt-sales',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/ryczalt/RyczaltSalesList.vue'),
            topRightSection: () => import('./views/pages/evidences/ryczalt/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-index',
            breadcrumb: [
              { title: 'Ryczalt sprzedaz', url: '/evidences/ryczalt-sales' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: DEDUCTION_DATE_FIELD,
          },
        },
        {
          path: '/evidences/ryczalt-purchases',
          name: 'evidences-ryczalt-purchases',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/ryczalt/RyczaltPurchasesList.vue'),
            topRightSection: () => import('./views/pages/evidences/ryczalt/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-index',
            breadcrumb: [
              { title: 'Ryczalt zakupy', url: '/evidences/ryczalt-purchases' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: DEDUCTION_DATE_FIELD,
          },
        },
        {
          path: '/evidences/ryczalt/add',
          name: 'evidences-ryczalt-add',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: (route) => {
              return {
                ...route.params,
                evidencetype: evidenceType.RYCZALT,
                hasFileModuleActive: hasFileModuleActive(route),
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
            // topRightSection: () => import('./views/pages/evidences/ryczalt/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesAdd,
            breadcrumb: [
              { title: 'Ewidencje', url: { name: 'evidences-ryczalt-index' } },
              { title: 'Ryczałty', url: { name: 'evidences-ryczalt-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/ryczalt/edit/:id(\\d+)',
          name: 'evidences-ryczalt-edit',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
                evidencetype: evidenceType.RYCZALT,
                hasFileModuleActive: hasFileModuleActive(route),
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesEdit,
            breadcrumb: [
              { title: 'Ewidencje', url: { name: 'evidences-ryczalt-index' } },
              { title: 'Ryczałty', url: { name: 'evidences-ryczalt-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/ryczalt/verify/:id(\\d+)',
          name: 'evidences-ryczalt-verify',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
                evidencetype: evidenceType.RYCZALT,
                hasFileModuleActive: hasFileModuleActive(route),
                isPreview: true,
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesEdit,
            breadcrumb: [
              { title: 'Ewidencje', url: { name: 'evidences-ryczalt-index' } },
              { title: 'Ryczałty', url: { name: 'evidences-ryczalt-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/all',
          name: 'evidences-all',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/all/AllEvidencesView.vue'),
            topRightSection: () => import('./views/pages/evidences/all/components/TopRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesAllList,
            breadcrumb: [
              { title: 'Księgowość', url: { name: 'evidences-all' } },
              { title: 'Rejestr wpisów', url: { name: 'evidences-all' } },
            ],
            dateFilterField: ENTRY_DATE_FIELD,
            rule: 'editor',
            search: true,
          },
        },

        // KH routes
        {
          path: '/evidences/kh',
          name: 'evidences-kh-index',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/kh/KhList.vue'),
            topRightSection: () => import('./views/pages/evidences/kh/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-index',
            breadcrumb: [
              { title: 'Kh', url: '/evidences/kh' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: ENTRY_DATE_FIELD,
          },
        },
        {
          path: '/evidences/kh-sales',
          name: 'evidences-kh-sales',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/kh/KhSalesList.vue'),
            topRightSection: () => import('./views/pages/evidences/kpir/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-sales',
            breadcrumb: [
              { title: 'Kh', url: '/evidences/kh' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: DEDUCTION_DATE_FIELD,
          },
        },
        {
          path: '/evidences/kh-purchases',
          name: 'evidences-kh-purchases',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/kh/KhPurchasesList.vue'),
            topRightSection: () => import('./views/pages/evidences/kpir/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-purchases',
            breadcrumb: [
              { title: 'Kh', url: '/evidences/kh' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: DEDUCTION_DATE_FIELD,
          },
        },
        {
          path: '/evidences/kh/add',
          name: 'evidences-kh-add',
          props: {
            default: (route) => {
              return {
                ...route.params,
                evidencetype: evidenceType.KH,
                hasFileModuleActive: hasFileModuleActive(route),
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesAdd,
            breadcrumb: [
              { title: 'Ewidencje', url: { name: 'evidences-kh-index' } },
              { title: 'KH', url: { name: 'evidences-kh-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/kh/edit/:id(\\d+)',
          name: 'evidences-kh-edit',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
                evidencetype: evidenceType.KH,
                hasFileModuleActive: hasFileModuleActive(route),
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesEdit,
            breadcrumb: [
              { title: 'Księgowość', url: { name: 'evidences-kh-index' } },
              { title: 'KH', url: { name: 'evidences-kh-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/kh/verify/:id(\\d+)',
          name: 'evidences-kh-verify',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
                evidencetype: evidenceType.KH,
                hasFileModuleActive: hasFileModuleActive(route),
                isPreview: true,
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesEdit,
            breadcrumb: [
              { title: 'Księgowość', url: { name: 'evidences-kh-index' } },
              { title: 'KH', url: { name: 'evidences-kh-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },

        // KPiR routes
        {
          path: '/evidences/kpir',
          name: 'evidences-kpir-index',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/kpir/KpirList.vue'),
            topRightSection: () => import('./views/pages/evidences/kpir/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-index',
            breadcrumb: [
              { title: 'Kpir', url: '/evidences/kpir' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: ENTRY_DATE_FIELD,
          },
        },
        {
          path: '/evidences/kpir-sales',
          name: 'evidences-kpir-sales',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/kpir/KpirSalesList.vue'),
            topRightSection: () => import('./views/pages/evidences/kpir/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-index',
            breadcrumb: [
              { title: 'Kpir sprzedaz', url: '/evidences/kpir-sales' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: DEDUCTION_DATE_FIELD,
          },
        },
        {
          path: '/evidences/kpir-purchases',
          name: 'evidences-kpir-purchases',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/kpir/KpirPurchasesList.vue'),
            topRightSection: () => import('./views/pages/evidences/kpir/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesList,
            paginationGroupName: 'evidences-index',
            breadcrumb: [
              { title: 'Kpir zakupy', url: '/evidences/kpir-purchases' },
            ],
            rule: 'editor',
            search: true,
            dateFilterField: DEDUCTION_DATE_FIELD,
          },
        },
        {
          path: '/evidences/kpir/add',
          name: 'evidences-kpir-add',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: (route) => {
              return {
                ...route.params,
                evidencetype: evidenceType.KPIR,
                hasFileModuleActive: hasFileModuleActive(route),
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
            // topRightSection: () => import('./views/pages/evidences/kpir/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesAdd,
            breadcrumb: [
              { title: 'Ewidencje', url: { name: 'evidences-kpir-index' } },
              { title: 'KPiR', url: { name: 'evidences-kpir-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/kpir/edit/:id(\\d+)',
          name: 'evidences-kpir-edit',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
                evidencetype: evidenceType.KPIR,
                hasFileModuleActive: hasFileModuleActive(route),
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesEdit,
            breadcrumb: [
              { title: 'Księgowość', url: { name: 'evidences-kpir-index' } },
              { title: 'KPiR', url: { name: 'evidences-kpir-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/kpir/verify/:id(\\d+)',
          name: 'evidences-kpir-verify',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
                evidencetype: evidenceType.KPIR,
                hasFileModuleActive: hasFileModuleActive(route),
                isPreview: true,
              };
            },
          },
          components: {
            default: () => import('./views/pages/evidences/components/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesEdit,
            breadcrumb: [
              { title: 'Księgowość', url: { name: 'evidences-kpir-index' } },
              { title: 'KPiR', url: { name: 'evidences-kpir-index' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/prints',
          name: 'evidences-prints',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/evidences/prints/PrintMenu.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageEvidencesPrints,
            breadcrumb: [
              { title: 'Księgowość', url: { name: 'evidences-prints' } },
              { title: 'Wydruki', url: { name: 'evidences-prints' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/invoices/kla',
          name: 'invoices-kla',
          components: {
            default: () => import('./views/pages/invoices_kla/List.vue'),
            topRightSection: () => import('./views/pages/invoices_kla/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesKlaList,
            paginationGroupName: 'invoices-index',
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices/kla' },
              { title: 'Automatyczne', url: '/invoices/kla' },
            ],
            rule: 'editor',
            search: true,
            module: 'INVOICES',
          },
        },
        {
          path: '/settlements',
          name: 'settlements',
          components: {
            default: () => import('./views/pages/settlements/List.vue'),
            topRightSection: () => import('./views/pages/settlements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageSettlementsList,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Rozliczenia', url: '/settlements' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
        },
        {
          path: '/settlements/checklist',
          name: 'main-settlements-checklist',
          components: {
            default: () => import('./views/pages/settlements/checklist/SettlementsChecklistView.vue'),
          },
          meta: {
            acl: AclName.CrmPageSettlementsList,
            breadcrumb: [
              { title: 'Checklista księgowa', url: '/settlements/checklist' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
        },
        {
          path: '/settlements/:id(\\d+)/checklist',
          name: 'detailed-settlements-checklist',
          components: {
            default: () => import('./views/pages/settlements/checklist/SettlementsChecklistDetailedView.vue'),
            topRightSection: () => import('./views/pages/settlements/checklist/components/TopRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageSettlementsList,
            breadcrumb: [
              { title: 'Checklista księgowa', url: '/settlements/checklist' },
              { title: 'Kontrahenci', url: '/settlements/:id/checklist' },
            ],
            rule: 'editor',
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/settlements/clients/:id',
          name: 'settlements-clients',
          props: {
            default: true,
            topRightSection: true,
          },
          components: {
            default: () => import('./views/pages/settlements/components/clients/List.vue'),
            topRightSection: () => import('./views/pages/settlements/components/clients/topRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageSettlementsClientsList,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Rozliczenia', url: '/settlements' },
              { title: 'Kontrahenci', url: '/settlements/clients/:clients_id' },
            ],
            rule: 'editor',
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/billing',
          name: RouteNames.billing.index,
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/billing/List.vue'),
            topRightSection: () => import('./views/pages/billing/components/topRightSection.vue'),
            popup: () => import('./views/pages/billing/components/PreviewBillingPopup.vue'),
          },
          meta: {
            acl: AclName.CrmPageBillingList,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Rozrachunki', url: '/billing' },
            ],
            rule: 'editor',
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/billing/settle/:id',
          name: RouteNames.billing.settle,
          meta: {
            acl: AclName.CrmPageBillingSettle,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Rozrachunki', url: '/billing' },
            ],
            search: false,
            module: 'CRM',
          },
          component: () => import('./views/pages/billing/components/Settle.vue'),
          props: (route) => ({ id: Number(route.params.id) }),
        },
        {
          path: '/mailing',
          name: 'mailing',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/mailing/List.vue'),
            topRightSection: () => import('./views/pages/mailing/components/MailingTopRightSection.vue'),
            popup: () => import('./views/pages/mailing/components/Popup.vue'),
          },
          meta: {
            acl: AclName.CrmPageMailingList,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Mailing', url: '/mailing' },
            ],
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/mailing/stepper',
          name: 'mailing-stepper',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/mailing/SendMailView.vue'),
          },
          meta: {
            acl: AclName.CrmPageMailingStepper,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Mailing', url: '/mailing' },
            ],
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/mailing/stepper/:id',
          name: 'mailing-stepper-edit',
          props: {
            default: (route) => ({
              id: ~~route.params.id,
            }),
          },
          components: {
            default: () => import('./views/pages/mailing/SendMailView.vue'),
          },
          meta: {
            acl: AclName.CrmPageMailingStepper,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Mailing', url: '/mailing' },
            ],
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/mailing/stepper/reply/:id',
          name: 'mailing-stepper-reply',
          props: {
            default: (route) => ({
              id: ~~route.params.id,
            }),
          },
          components: {
            default: () => import('./views/pages/mailing/SendMailView.vue'),
          },
          meta: {
            acl: AclName.CrmPageMailingStepper,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Mailing', url: '/mailing' },
            ],
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/mailing/stepper/forward/:id',
          name: 'mailing-stepper-forward',
          props: {
            default: (route) => ({
              id: ~~route.params.id,
            }),
          },
          components: {
            default: () => import('./views/pages/mailing/SendMailView.vue'),
          },
          meta: {
            acl: AclName.CrmPageMailingStepper,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Mailing', url: '/mailing' },
            ],
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/mailing/stepper/forward-own/:id',
          name: 'mailing-stepper-forward-own',
          props: {
            default: (route) => ({
              id: ~~route.params.id,
            }),
          },
          components: {
            default: () => import('./views/pages/mailing/SendMailView.vue'),
          },
          meta: {
            acl: AclName.CrmPageMailingStepper,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Mailing', url: '/mailing' },
            ],
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/mailing/edit/:id',
          name: 'mailing-edit',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/mailing/components/MailingForm.vue'),
          },
          meta: {
            acl: AclName.CrmBtnMailingEdit,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Mailing', url: '/mailing' },
            ],
            rule: 'editor',
            search: true,
            module: 'CRM',
          },
          rule: 'editor',
          search: false,
        },
        {
          path: '/invoices/add',
          name: 'invoices-add',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/invoices/addEditWrapper.vue'),
            // topRightSection: () => import('./views/pages/employees/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesAdd,
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices' },
              { title: 'Faktury', url: '/invoices' },
            ],
            rule: 'editor',
            search: true,
            module: 'INVOICES',
          },
        },
        {
          path: '/invoices/edit/:id',
          name: 'invoices-edit',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/invoices/addEditWrapper.vue'),
            // topRightSection: () => import('./views/pages/employees/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesEdit,
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices' },
              { title: 'Faktury', url: '/invoices' },
            ],
            rule: 'editor',
            search: true,
            module: 'INVOICES',
          },
          rule: 'editor',
          search: false,
        },
        {
          path: '/invoices/correction/:id(\\d+)',
          name: 'invoices-correction',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/invoices/AddCorrection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesCorrectionEdit,
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices' },
              { title: 'Faktury', url: '/invoices' },
            ],
            search: false,
            module: 'INVOICES',
          },
          rule: 'editor',
          search: false,
        },
        {
          path: '/invoices/correction/edit/:id(\\d+)',
          name: 'invoices-correction-edit',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/invoices/EditCorrection.vue'),
            // topRightSection: () => import('./views/pages/employees/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesCorrectionEdit,
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices' },
              { title: 'Faktury', url: '/invoices' },
              { title: 'Korekty', url: '/invoices' },
            ],
            rule: 'editor',
            search: true,
            module: 'INVOICES',
          },
          rule: 'editor',
          search: false,
        },
        {
          path: '/invoices/kla/edit/:id',
          name: 'invoices-kla-edit',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/invoices_kla/addEdit.vue'),
            // topRightSection: () => import('./views/pages/employees/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageInvoicesKlaEdit,
            breadcrumb: [
              { title: 'Fakturowanie', url: '/invoices/kla' },
              { title: 'Faktury automatyczne KLA', url: '/invoices/kla' },
            ],
            rule: 'editor',
            search: true,
            module: 'INVOICES',
          },
          rule: 'editor',
          search: false,
        },
        {
          path: '/vehicles',
          name: RouteNames.vehicles.listView,
          components: {
            default: () => import('./views/pages/vehicles/VehicleList.vue'),
            topRightSection: () => import('./views/pages/vehicles/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageVehiclesList,
            breadcrumb: [
              { title: 'Księgowość', url: '/vehicles' },
              { title: 'Pojazdy', url: '/vehicles' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/vehicles/add',
          name: 'vehicles-add',
          components: {
            default: () => import('./views/pages/vehicles/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageVehiclesAdd,
            breadcrumb: [
              { title: 'Księgowość', url: '/vehicles' },
              { title: 'Pojazdy', url: '/vehicles' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/vehicles/edit/:id',
          name: RouteNames.vehicles.editView,
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/vehicles/addEdit.vue'),
            // topRightSection: () => import('./views/pages/clients/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageVehiclesEdit,
            breadcrumb: [
              { title: 'Księgowość', url: '/vehicles' },
              { title: 'Pojazdy', url: '/vehicles' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          // PUB-1649 — dokumenty pojazdu; wystarczy prawo do listy pojazdów (także klient biura).
          path: '/vehicles/:id(\\d+)/documents',
          name: RouteNames.vehicles.documentsView,
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/vehicles/VehicleDocumentsView.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageVehiclesList,
            breadcrumb: [
              { title: 'Księgowość', url: '/vehicles' },
              { title: 'Pojazdy', url: '/vehicles' },
            ],
            pageTitle: 'Dokumenty pojazdu',
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/clients',
          name: 'clients',
          // component: () => import('./views/pages/clients/List.vue'),
          components: {
            default: () => import('./views/pages/clients/List.vue'),
            topRightSection: () => import('./views/pages/clients/components/CrmListTopRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageCrmList,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Kontrahenci', url: '/clients' },
            ],
            rule: 'editor',
            search: true,
            module: 'CRM',
          },
        },
        {
          path: '/clients/add',
          name: 'clients-add',
          components: {
            default: () => import('./views/pages/clients/addEdit.vue'),
          },
          meta: {
            acl: AclName.CrmPageCrmAdd,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Kontrahenci', url: '/clients' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
        },
        {
          path: '/clients/stepper',
          name: 'clients-stepper',
          components: {
            default: () => import('./views/pages/clients/ClientCreateView.vue'),
          },
          meta: {
            acl: AclName.CrmPageCrmAdd,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Kontrahenci', url: '/clients' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
        },
        {
          path: '/clients/import',
          name: 'clients-import',
          components: {
            default: () => import('./modules/clients-import/Import.vue'),
          },
          meta: {
            acl: AclName.CrmBtnClientsImport,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Kontrahenci', url: '/clients' },
              { title: 'Import', url: '/clients/import' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
        },
        {
          path: '/clients/edit/:id',
          name: 'clients-edit',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/clients/addEdit.vue'),
          },
          meta: {
            acl: AclName.CrmPageCrmEdit,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Kontrahenci', url: '/clients' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
        },
        {
          path: '/assignments',
          name: 'assignments',
          components: {
            default: () => import('./views/pages/assignments/List.vue'),
          },
          meta: {
            acl: AclName.CrmPageAssignments,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Przypisania', url: '/assignments' },
            ],
            rule: 'editor',
            search: true,
            module: 'CRM',
          },
        },

        // ---------------UMOWY KONTRAHENTÓW-----------------------------
        {
          path: '/clients/agreements',
          name: 'clients-agreements',
          components: {
            default: () => import('./views/pages/clients_agreements/List.vue'),
            topRightSection: () => import('./views/pages/clients_agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageAgreementsList,
            breadcrumb: [
              { title: 'CRM', url: '/clients/agreements' },
              { title: 'Umowy', url: '/clients/agreements' },
            ],
            rule: 'editor',
            search: true,
            module: 'CRM',
          },

        },
        {
          path: '/clients/agreements/add/:crm_id(\\d+)?',
          name: 'clients/agreements-add',
          components: {
            default: () => import('./views/pages/clients_agreements/addEdit.vue'),
            // topRightSection: () => import('./views/pages/clients_agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageAgreementsAdd,
            breadcrumb: [
              { title: 'CRM', url: '/clients/agreements' },
              { title: 'Umowy', url: '/clients/agreements' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
          props: {
            default: (route) => {
              let crm_id = _.get(route, 'params.crm_id');
              crm_id = crm_id ? ~~crm_id : null;

              return {
                ...route.params,
                crm_id: crm_id || undefined,
              };
            },

          },
        },
        {
          path: '/clients/agreements/edit/:id',
          name: 'clients/agreements-edit',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/clients_agreements/addEdit.vue'),
            // topRightSection: () => import('./views/pages/clients_agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageAgreementsEdit,
            breadcrumb: [
              { title: 'CRM', url: '/clients/agreements' },
              { title: 'Umowy', url: '/clients/agreements' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
        },
        // --------------------------------------------

        {
          path: '/profile/',
          name: 'user-preview',
          props: (route) => ({ id: Number(route.params.id) }),
          components: {
            default: () => import('./views/pages/users/profile.vue'),
          },
          meta: {
            breadcrumb: [],
            rule: 'editor',
            search: false,
          },
          beforeEnter: beforeProfileGuard,
        },
        {
          path: '/profile/edit',
          name: 'user-edit',
          // props: {
          //     default: true
          // },
          components: {
            default: () => import('./views/pages/users/editProfile'),
            // topRightSection: () => import('./views/pages/clients/components/topRightSection.vue'),
          },
          beforeEnter: beforeProfileGuard,
          meta: {
            breadcrumb: [
              { title: 'Profil', url: '/profile' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/crm/:id?',
          name: 'crm',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/crm/List.vue'),
            topRightSection: () => import('./views/pages/crm/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageCrmSectionList,
            breadcrumb: [
              { title: 'CRM', url: '/clients' },
              { title: 'Kontrahenci', url: '/clients' },
            ],
            rule: 'editor',
            search: false,
            module: 'CRM',
          },
          beforeEnter: beforeCrmEnter,
        },
        {
          path: '/todo/',
          name: 'todo',

          components: {
            default: () => import('./views/pages/crm/todo/Todo.vue'),
          },
          meta: {
            acl: AclName.CrmPageTodoList,
            breadcrumb: [
              { title: 'CRM', url: '/todo/all' },
              { title: 'Zadania', url: '/todo/all' },
            ],
            rule: 'admin',
            search: false,
            module: 'CRM',
          },
        },
        {
          path: '/todo/:filter',
          components: {
            default: () => import('./views/pages/crm/todo/Todo.vue'),
            // topRightSection: () => import('./views/pages/crm/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.CrmPageTodoList,
            breadcrumb: [
              { title: 'CRM', url: '/todo/all' },
              { title: 'Zadania', url: '/todo/all' },
            ],
            rule: 'editor',
            parent: 'todo',
            no_scroll: true,
            module: 'CRM',
          },
        },
        {
          path: '/employees',
          name: 'employees',
          components: {
            default: () => import('./views/pages/employees/List.vue'),
            topRightSection: () => import('./views/pages/employees/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageEmployeesList,
            breadcrumb: [
              { title: 'Kadry', url: '/employees' },
              { title: 'Pracownicy', url: '/employees' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/employees/add',
          name: 'employees-add',
          components: {
            default: () => import('./views/pages/employees/addEdit.vue'),
            // topRightSection: () => import('./views/pages/employees/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageEmployeesAdd,
            breadcrumb: [
              { title: 'Kadry', url: '/employees' },
              { title: 'Pracownicy', url: '/employees' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/employees/edit/:id',
          name: 'employees-edit',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/employees/addEdit.vue'),
            // topRightSection: () => import('./views/pages/employees/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageEmployeesEdit,
            breadcrumb: [
              { title: 'Kadry', url: '/employees' },
              { title: 'Pracownicy', url: '/employees' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        //
        {
          path: '/payroll',
          name: 'payroll',
          // component: () => import('./views/pages/clients/List.vue'),
          components: {
            default: () => import('./views/pages/payroll/List.vue'),
            topRightSection: () => import('./views/pages/payroll/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPagePayrollList,
            breadcrumb: [
              { title: 'Kadry', url: '/payroll' },
              { title: 'Płace', url: '/payroll' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/payroll/worker-list/:id',
          name: 'payroll-worker-list',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/payroll/components/employees/List.vue'),
            topRightSection: () => import('./views/pages/payroll/components/employees/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPagePayrollEmployeesList,
            breadcrumb: [
              { title: 'Kadry', url: '/payroll' },
              { title: 'Płace', url: '/payroll' },
              { title: 'Pracownicy', url: '/payroll/worker-list/:worker_list_id' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/payroll/worker-list/edit/:id',
          name: 'payroll-worker-edit',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
              };
            },
          },
          components: {
            default: () => import('./views/pages/payroll/components/employees/PaymentAddEdit.vue'),
            // topRightSection: () => import('./views/pages/payroll/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPagePayrollEmployeesEdit,
            breadcrumb: [
              { title: 'Kadry', url: '/payroll' },
              { title: 'Płace', url: '/payroll/' },
              { title: 'Pracownicy', url: '/payroll/worker-list/:worker_list_id' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/payroll/worker-list/recalculate/:id',
          name: 'payroll-worker-regenerate',
          props: {
            default: (route) => {
              return {
                ...route.params,
                id: ~~route.params.id,
              };
            },
          },
          components: {
            default: () => import('./views/pages/payroll/components/employees/RecalculateAddEdit.vue'),
            // topRightSection: () => import('./views/pages/payroll/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPagePayrollEmployeesRecalculate,
            breadcrumb: [
              { title: 'Kadry', url: '/payroll' },
              { title: 'Płace', url: '/payroll/' },
              { title: 'Pracownicy', url: '/payroll/worker-list/:worker_list_id' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        // bills
        {
          path: '/bills',
          name: 'bills-list',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/bills/List'),
            topRightSection: () => import('./views/pages/bills/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageBillsList,
            breadcrumb: [
              { title: 'Kadry', url: '/payroll' },
              { title: 'Rachunki', url: '/bills' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/bills/edit/:id',
          name: 'bill-edit',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/bills/components/employees/DataPaid'),
            // topRightSection: () => import('./views/pages/bills/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageBillsEdit,
            breadcrumb: [
              { title: 'Kadry', url: '/payroll' },
              { title: 'Rachunki', url: '/bills' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/bills/generate',
          name: 'bills-generate',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          beforeEnter: beforeBillGeneratorEnter,
          components: {
            default: () => import('./views/pages/bills/components/generate/List'),
            topRightSection: () => import('./views/pages/bills/components/generate/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageBillsGenerate,
            breadcrumb: [
              { title: 'Kadry', url: '/payroll' },
              { title: 'Rachunki', url: '/bills' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        // -------------------------------------------------
        {
          path: '/agreements',
          name: 'agreements',
          // component: () => import('./views/pages/clients/List.vue'),
          components: {
            default: () => import('./views/pages/agreements/List.vue'),
            topRightSection: () => import('./views/pages/agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageAgreementsDeductionsList,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
            ],
            rule: 'editor',
            search: true,
          },

        },
        {
          path: '/agreements/deductions',
          name: 'agreements-deductions',
          // component: () => import('./views/pages/clients/List.vue'),
          components: {
            default: () => import('./views/pages/agreements/deductions/List.vue'),
            topRightSection: () => import('./views/pages/agreements/deductions/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageAgreementsDeductionsList,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
              { title: 'Potrącenia', url: '/agreements/deductions' },
            ],
            rule: 'editor',
            search: true,
          },

        },
        {
          path: '/agreements/deductions/add',
          name: 'agreements-deductions-add',
          components: {
            default: () => import('./views/pages/agreements/deductions/addEdit.vue'),
            // topRightSection: () => import('./views/pages/agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageAgreementsDeductionsAdd,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
              { title: 'Potrącenia', url: '/agreements/deductions' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/agreements/deductions/edit/:id',
          name: 'agreements-deductions-edit',
          components: {
            default: () => import('./views/pages/agreements/deductions/addEdit.vue'),
            // topRightSection: () => import('./views/pages/agreements/components/topRightSection.vue'),
          },
          props: {
            default: true,
          },
          meta: {
            acl: AclName.StaffPageAgreementsDeductionsEdit,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
              { title: 'Potrącenia', url: '/agreements/deductions' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/agreements/add',
          name: 'agreements-add',
          components: {
            default: () => import('./views/pages/agreements/addEdit.vue'),
            // topRightSection: () => import('./views/pages/agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageAgreementsAdd,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/agreements/edit/:id',
          name: 'agreements-edit',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/agreements/addEdit.vue'),
            // topRightSection: () => import('./views/pages/agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageAgreementsEdit,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/agreements/annex/add/:id',
          name: 'agreements-annex-add',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/agreements/AddAnnex.vue'),
          },
          meta: {
            acl: AclName.StaffPageAgreementsAdd,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/agreements/annex/edit/:id',
          name: 'agreements-annex-edit',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/agreements/EditAnnex.vue'),
            // topRightSection: () => import('./views/pages/agreements/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageAgreementsEdit,
            breadcrumb: [
              { title: 'Kadry', url: '/agreements' },
              { title: 'Umowy', url: '/agreements' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/employees/prints',
          name: 'employees-prints',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/employees/prints/PrintMenu.vue'),
          },
          meta: {
            acl: AclName.StaffPageEmployeesList,
            breadcrumb: [
              { title: 'Kadry', url: { name: 'employees' } },
              { title: 'Wydruki', url: { name: 'employees-prints' } },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/declarations/pit',
          name: 'declarations-pit',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/declarations/pit/DeclarationPitList.vue'),
            topRightSection: () => import('./views/pages/declarations/pit/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.StaffPageDeclarationsPitList,
            breadcrumb: [
              { title: 'Deklaracje', url: '/declarations/pit' },
              { title: 'PIT', url: '/declarations/pit' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/declarations/company-zus',
          name: 'declarations-company-zus-list',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/zusCompany/List.vue'),
            topRightSection: () => import('./views/pages/zusCompany/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageZusCompanySettlementsList,
            breadcrumb: [
              { title: 'ZUS', url: '/declarations/company-zus' },
            ],
            rule: 'editor',
            search: true,
          },
          beforeEnter: beforeZusEnterMiddleware,
        },
        {
          path: '/declarations/zus-company/add',
          name: 'declarations-company-zus-add',
          components: {
            default: () => import('./views/pages/zusCompany/addEdit.vue'),
          },
          props: {
            default: true,
          },
          meta: {
            acl: AclName.BookkeepingPageZusCompanySettlementsAdd,
            breadcrumb: [
              { title: 'Rozliczenia ZUS', url: '/declarations/company-zus' },
            ],
            rule: 'editor',
            search: true,
          },
          beforeEnter: beforeZusEnterMiddleware,
        },
        {
          path: '/declarations/zus-company/edit/:id',
          name: 'declarations-company-zus-edit',
          components: {
            default: () => import('./views/pages/zusCompany/addEdit.vue'),
          },
          props: {
            default: true,
          },
          meta: {
            acl: AclName.BookkeepingPageZusCompanySettlementsEdit,
            breadcrumb: [
              { title: 'Rozliczenia ZUS', url: '/declarations/company-zus' },
            ],
            rule: 'editor',
            search: true,
          },
          beforeEnter: beforeZusEnterMiddleware,
        },
        {
          path: '/declarations/vat',
          name: 'declarations-vat',
          // props: (route) => ({id: Number(route.params.id)}),
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/declarations/vat/DeclarationVatList.vue'),
            topRightSection: () => import('./views/pages/declarations/vat/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageDeclarationsVatList,
            breadcrumb: [
              { title: 'Deklaracje', url: '/declarations/vat' },
              { title: 'VAT', url: '/declarations/vat' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/closed-month',
          name: 'evidences-closed-month',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/closed_month/List.vue'),
            topRightSection: () => import('./views/pages/closed_month/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageClosedMonthList,
            breadcrumb: [
              { title: 'Księgowość', url: '/evidences/closed-month' },
              { title: 'Zamknięte miesiące', url: '/evidences/closed-month' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/closed-month/add',
          name: 'evidences-closed-month-add',
          components: {
            default: () => import('./views/pages/closed_month/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageClosedMonthAdd,
            breadcrumb: [
              { title: 'Księgowość', url: '/evidences/closed-month' },
              { title: 'Zamknięte miesiące', url: '/evidences/closed-month' },
            ],
            rule: 'editor',
          },
        },
        {
          path: '/evidences/closed-month/edit/:id(\\d+)',
          name: 'evidences-closed-month-edit',
          components: {
            default: () => import('./views/pages/closed_month/addEdit.vue'),
          },
          props: {
            default: (route) => {
              let id = _.get(route, 'params.id');
              if (id) {
                id = ~~id;
              }
              return {
                ...route.params,
                id,
              };
            },
          },
          meta: {
            acl: AclName.BookkeepingPageClosedMonthEdit,
            breadcrumb: [
              { title: 'Księgowość', url: '/evidences/closed-month' },
              { title: 'Zamknięte miesiące', url: '/evidences/closed-month' },
            ],
            rule: 'editor',
          },
        },
        {
          path: '/evidences/closed-month/tax-summary/:id(\\d+)',
          name: 'evidences-closed-month-tax-summary',
          components: {
            default: () => import('./views/pages/closed_month/TaxSummaryPreview.vue'),
          },
          props: {
            default: true,
          },
          meta: {
            acl: AclName.BookkeepingPageClosedMonthTaxesInfo,
            breadcrumb: [
              { title: 'Księgowość', url: '/evidences/closed-month' },
              { title: 'Zamknięte miesiące', url: '/evidences/closed-month' },
            ],
            rule: 'editor',
          },
        },
        {
          path: '/admin/panel',
          name: 'admin-panel',
          component: () => import('./views/pages/admin/panel/Index.vue'),
          meta: {
            acl: AclName.SettingsPageAdminPanel,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Panel', url: '/admin/panel' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/admin/price-lists/:id(\\d+)',
          name: 'admin-price-lists-list',
          component: () => import('./views/pages/admin/PriceLists/TemplatePriceListEditableView'),
          meta: {
            acl: AclName.SettingsPagePriceListsEdit,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Cenniki', url: '/admin/price-lists' },
              { title: 'Edycja', url: '' },
            ],
          },
        },
        {
          path: EVIDENCE_DESCRIPTIONS_ROUTES.EVIDENCE_DESCRIPTIONS_LIST.path,
          name: EVIDENCE_DESCRIPTIONS_ROUTES.EVIDENCE_DESCRIPTIONS_LIST.name,
          components: {
            default: () => import('./views/pages/admin/templates/evidence/TemplateEvidenceList.vue'),
            topRightSection: () => import('./views/pages/admin/templates/evidence/components/EvidenceDescriptionTemplateTopRightSection.vue'),
          },
          meta: {
            acl: AclName.SettingsPageTemplatesEvidenceDescriptionsList,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Szablony', url: '' },
              { title: 'Ewidencje', url: '/admin/templates/evidence-descriptions' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          // PUB-1649 — katalog kategorii repozytorium Dokumenty (kategorie firmowe).
          path: DOCUMENT_CATEGORIES_ROUTES.DOCUMENT_CATEGORIES_LIST.path,
          name: DOCUMENT_CATEGORIES_ROUTES.DOCUMENT_CATEGORIES_LIST.name,
          components: {
            default: () => import('./views/pages/admin/templates/document-categories/DocumentCategoriesList.vue'),
          },
          meta: {
            breadcrumb: [
              { title: 'Panel', url: DOCUMENT_CATEGORIES_ROUTES.DOCUMENT_CATEGORIES_LIST.path },
              { title: 'Szablony', url: DOCUMENT_CATEGORIES_ROUTES.DOCUMENT_CATEGORIES_LIST.path },
            ],
            pageTitle: 'Kategorie dokumentów',
            rule: 'editor',
            // To samo uprawnienie, co w menu: kategorie firmowe należą do modułu Dokumenty.
            acl: AclName.FilesPageFilesList,
          },
        },
        {
          path: '/admin/templates/evidence-descriptions/add',
          name: 'evidence-description-template-add',
          components: {
            default: () => import('./views/pages/admin/templates/evidence/addEdit.vue'),
          },
          meta: {
            acl: AclName.SettingsPageTemplatesEvidenceDescriptionsList,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Szablony', url: '' },
              { title: 'Ewidencje', url: '/admin/templates/evidence-descriptions' },
              { title: 'Dodawanie', url: '' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/admin/templates/evidence-descriptions/add-subcategory/:id',
          name: routeName.evidenceDescriptionTemplateAddSubcategory,
          components: {
            default: () => import('./views/pages/admin/templates/evidence/addEdit.vue'),
          },
          meta: {
            acl: AclName.SettingsPageTemplatesEvidenceDescriptionsList,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Szablony', url: '' },
              { title: 'Ewidencje', url: '/admin/templates/evidence-descriptions' },
              { title: 'Podkategoria', url: '' },
            ],
            rule: 'editor',
            search: true,
          },
          props: {
            default: true,
          },
        },
        {
          path: '/admin/templates/evidence-descriptions/edit/:id',
          name: 'evidence-description-template-edit',
          components: {
            default: () => import('./views/pages/admin/templates/evidence/addEdit.vue'),
          },
          meta: {
            acl: AclName.SettingsPageTemplatesEvidenceDescriptionsList,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Szablony', url: '' },
              { title: 'Ewidencje', url: '/admin/templates/evidence-descriptions' },
              { title: 'Edycja', url: '' },
            ],
            rule: 'editor',
            search: true,
          },
          props: {
            default: true,
          },
        },
        {
          path: '/admin/website',
          name: 'admin-website',
          redirect: '/admin/website/pagebuilder',
          meta: {
            acl: AclName.WebsitesBtnEdit,
          },
        },
        {
          path: '/admin/website/pagebuilder',
          name: 'admin-website-pagebuilder-redirect',
          component: () => import('./views/pages/admin/website/PageBuilderRedirect.vue'),
          meta: {
            acl: AclName.WebsitesBtnEdit,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Strona www', url: '/admin/website/pagebuilder' },
              { title: 'PageBuilder', url: '' },
            ],
          },
        },
        {
          path: '/account-redirecting',
          name: 'account-redirecting',
          component: () => import('./views/pages/AccountLandingPage/AccountRedirectingPage.vue'),
        },
        {
          path: '/evidences/fixed-assets',
          name: 'fixed-assets',
          components: {
            default: () => import('./views/pages/fixed-assets/FixedAssetsList.vue'),
            topRightSection: () => import('./views/pages/fixed-assets/components/topRightSection.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageFixedAssetsList,
            breadcrumb: [
              { title: 'Księgowość', url: '/evidences/fixed-assets' },
              { title: 'Środki trwałe', url: '/evidences/fixed-assets' },
            ],
            rule: 'editor',
            search: true,
          },
        },
        {
          path: '/evidences/fixed-assets/add',
          name: 'fixed-assets-add',
          components: {
            default: () => import('./views/pages/fixed-assets/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageFixedAssetsAdd,
            breadcrumb: [
              { title: 'Księgowość', url: '/evidences/fixed-assets' },
              { title: 'Środki trwałe', url: '/evidences/fixed-assets' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/evidences/fixed-assets/edit/:id',
          name: 'fixed-assets-edit',
          props: {
            default: true,
          },
          components: {
            default: () => import('./views/pages/fixed-assets/addEdit.vue'),
          },
          meta: {
            acl: AclName.BookkeepingPageFixedAssetsEdit,
            breadcrumb: [
              { title: 'Księgowość', url: '/evidences/fixed-assets' },
              { title: 'Środki trwałe', url: '/evidences/fixed-assets' },
            ],
            rule: 'editor',
            search: false,
          },
        },
        {
          path: '/admin/templates/invoice-descriptions/add',
          name: 'invoices-description-template-add',
          components: {
            default: () => import('./views/pages/invoice-description-templates/addEdit.vue'),
          },
          meta: {
            acl: AclName.SettingsPageTemplatesInvoiceDescriptionsEdit,
            rule: 'editor',
            search: false,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Szablony', url: '' },
              { title: 'Opis faktur', url: '/admin/templates/invoice-descriptions' },
              { title: 'Dodawanie', url: '' },
            ],
          },
          props: {
            default: true,
          },
        },
        {
          path: '/admin/templates/invoice-descriptions/edit/:id',
          name: 'invoices-description-template-edit',
          components: {
            default: () => import('./views/pages/invoice-description-templates/addEdit.vue'),
          },
          meta: {
            rule: 'editor',
            search: false,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Szablony', url: '' },
              { title: 'Opis faktur', url: '/admin/templates/invoice-descriptions' },
              { title: 'Edycja', url: '' },
            ],
          },
          props: {
            default: true,
          },
        },
        {
          path: '/admin/mailing/settings',
          name: 'admin-mailing-settings',
          component: () => import('./views/pages/admin/mailing/Settings.vue'),
          meta: {
            acl: AclName.MailingSettingsBtnSave,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Mailing', url: '' },
              { title: 'Konfiguracja', url: '/admin/mailing/settings' },
            ],
          },
        },
        {
          path: '/admin/mailing/footer',
          name: 'admin-mailing-footer',
          component: () => import('./views/pages/admin/mailing/MailFooter.vue'),
          meta: {
            acl: AclName.MailingSettingsBtnSave,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'Mailing', url: '' },
              { title: 'Stopka', url: '/admin/mailing/footer' },
            ],
          },
        },
        {
          path: '/admin/ksef/settings',
          name: 'admin-ksef-settings',
          component: () => import('./modules/ksef/settings/views/KsefSettingsView.vue'),
          meta: {
            acl: AclName.CanViewKsefSettings,
            breadcrumb: [
              { title: 'Admin', url: '' },
              { title: 'KSeF', url: '' },
              { title: 'Konfiguracja', url: '/admin/ksef/settings' },
            ],
          },
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    ...routes,
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/login',
          name: 'login',
          component: () => import('./views/pages/login/Login.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          // Transient splash that re-bootstraps the session from the server (fresh person/scopes)
          // after onboarding finish, then redirects to the panel.
          path: '/bootstrap',
          name: 'bootstrap',
          component: () => import('./views/pages/Bootstrap.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/register',
          name: 'register',
          component: () => import('./views/pages/register/RegisterAccountingOffice.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/rejestracja',
          name: 'rejestracja',
          redirect: to => {
            return {
              path: '/register',
            };
          },
        },
        {
          path: '/invite/:hash',
          name: 'invite-aoc',
          component: () => import('./views/pages/register/RegisterAccountingOfficeClient.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/invitation',
          name: 'invitation',
          component: () => import('./views/pages/register/Invitation.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/forgot-password',
          name: 'forgot-password',
          component: () => import('./views/pages/ForgotPassword.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/reset-password',
          name: 'reset-password',
          component: () => import('./views/pages/ResetPassword.vue'),
          beforeEnter: beforeResetPasswordEnter,
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/reset-password-link-expired/:email',
          name: 'reset-password-link-expired',
          props: {
            default: true,
          },
          component: () => import('./views/pages/ResetPasswordLinkExpired.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/401',
          name: 'page-error-401',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
        {
          path: '/mail-attachment/:mailOriginal/:downloadToken',
          name: 'mail-attachment',
          component: () => import('./views/pages/MailAttachement.vue'),
          meta: {
            fullResolutionSupport: true,
          },
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404',
    },
  ],
});

[
  filterMiddleware,
  crmNavigationMiddleware,
].forEach((middleware) => router.afterEach(middleware));

// Block panel access until onboarding is finished: on every guarded navigation ask /me and
// bounce to the wizard while required && !completed; cache the pass so we stop polling.
let onboardingPassed = false;

async function redirectsToOnboarding(to, publicNames) {
  if (onboardingPassed) return false;
  if (localStorage.getItem('token') === null || publicNames.includes(to.name)) return false;
  try {
    const { data } = await OnboardingApi.me();
    if (data && data.required && !data.completed) {
      window.location.href = `${(import.meta.env.VITE_APP_BASE_URL || '').replace(/\/+$/, '')}/onboarding/`;
      return true;
    }
    onboardingPassed = true;
  } catch (e) { /* non-blocking: a failing /onboarding/me must never block the app */ }
  return false;
}

router.beforeEach(async (to, from, next) => {
  PopupProvider.clearPopup();
  getPageIcon(to);
  getPageTitle(to);

  let avaliableNames = [
    'login',
    'bootstrap',
    'register',
    'rejestracja',
    'invite-aoc',
    'invitation',
    'start',
    'forgot-password',
    'reset-password',
    'reset-password-link-expired',

    'home-page',
    'home-privacy-policy',
    'home-service-regulations',
    'home-changediary',

    'mail-attachment',
  ];

  const acl = to?.meta?.acl;
  if (acl && !store.getters['auth/aclHave'](acl)) {
    next({ name: 'page-error-401' });
  }

  if (localStorage.getItem('token') === null && !avaliableNames.includes(to.name)) {
    next('/home');
  } else {
    // Query-only navigations (pagination/filters/sort keep the same route name) must resolve
    // synchronously — the pagination mixin reads $route right after $router.push. Only gate real
    // page transitions behind the awaited onboarding check; short-circuit skips the await otherwise.
    if (to.name !== from.name && await redirectsToOnboarding(to, avaliableNames)) {
      return;
    }
    RouterStorageService.set(to.name, to);
    next();
  }
});

function getPageIcon(to) {
  if (to.meta.icon != undefined) {
    const link = document.querySelector('[rel=\'shortcut icon\']');
    link.setAttribute('href', to.meta.icon);
  }
}

function getPageTitle(to) {
  if (to.meta.title == undefined) {
    document.title = 'PuzzleTax';
  } else {
    document.title = to.meta.title;
  }
}

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

async function beforeProfileGuard(to, from, next) {
  await store.dispatch('auth/switchToMainAccount').then(() => {
    store.commit('auth/refreshAccountTabs');
    return next();
  });
}

function beforeCrmEnter(to, from, next) {
  store.dispatch('CRM_MODULE/clearCrm');
  store.dispatch('CRM_MODULE/clearCrmIdList');
  store.dispatch('CRM_MODULE/setStatusFilter', {});
  next();
}

function beforeBillGeneratorEnter(to, from, next) {
  store.dispatch('STAFF_MODULE/clearBillGeneratorData');
  next();
}

function beforeResetPasswordEnter(to, from, next) {
  let email = to.query.email;
  Axios.post(import.meta.env.VITE_SITE_URL + '/password/check-token', {
    token: to.query.token,
    email: email,
  })
    .then(() => {
      next();
    })
    .catch(() => {
      router.push({ name: 'reset-password-link-expired', params: { email } });
    });
}

Axios.interceptors.response.use(
  (response) => {
    isSystemBalanceRefreshRequired(response?.config?.method, response?.config?.url)
    && store.dispatch('SYSTEM_BALANCE/current');

    return response;
  },
  function (error) {
    const pathsDisabledFromRedirect = [
      '/mail-attachments/',
    ];

    if (401 === error?.response?.status && !pathsDisabledFromRedirect.some((path) => error?.response?.config?.url.includes(path))) {
      store.dispatch('auth/logout');
      router.push('/login').catch(() => { });
    }
    return Promise.reject(error);
  },
);

export default router;
